import { useRef, useState } from 'react';
import axios from 'axios';
import Modal from './Modal';
import Form from '../Form/Form';
import { toast } from 'react-toastify';
import InputText from '../Form/Fields/InputText';
import Select from '../Form/Fields/Select';
import { getPaymentMethodsOptions } from '../../config/PaymentMethod';
import Button from '../Form/Button';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';
import { useNavigate } from 'react-router-dom';
import { toastCreated } from '../../utils/ToastLink';

const formatAmount = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const NewInvoiceModal = ({ order, patientId, show, onClose, onSuccess }) => {
    const navigate = useNavigate();
    const modalRef = useRef();
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = (data) => {
        setIsLoading(true);

        const payload = {
            paymentMethod: data.paymentMethod,
            amount: data.invoiceAmount,
        };

        let url;
        if (order) {
            url = `${API_BASE_URL}/api/v1/orders/${order.id}/invoices`;
        } else if (patientId) {
            url = `${API_BASE_URL}/api/v1/patients/${patientId}/invoices/bulk`;
        } else {
            toast.error("Aucune vente ou patient fourni");
            setIsLoading(false);
            return;
        }

        axios.post(url, payload, { withCredentials: true })
            .then((response) => {
                // Only the single-order path returns one Invoice with an id to link to —
                // the patient "bulk" path settles several invoices at once, no single target.
                toastCreated(navigate, 'Paiement enregistré avec succès.', order && response.data?.id && `/invoices/${response.data.id}`);
                modalRef.current?.hide();
                setIsLoading(false);
                if (onSuccess) onSuccess();
            })
            .catch(err => {
                toast.error(err.response?.data?.message || 'Une erreur est survenue');
                console.error(err);
                setIsLoading(false);
            });
    };

    const remaining = order ? (order.total ?? 0) - (order.paiedAmount ?? 0) : null;

    return (
        <Modal
            ref={modalRef}
            id="createInvoiceModal"
            classSize="modal-sm modal-dialog-centered"
            title="Nouveau paiement"
            icon={faFileInvoiceDollar}
            iconBg="#eff6ff"
            iconColor="#1d4ed8"
            show={show}
            onClose={onClose}
            footer={
                <Button
                    isLoading={isLoading}
                    loadingMessage="Enregistrement…"
                    type="submit"
                    form="NewInvoiceForm"
                    className="btn btn-primary w-100 fw-bold"
                >
                    Enregistrer le paiement
                </Button>
            }
        >
            {order && (
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: 10, padding: '10px 14px', marginBottom: 20,
                }}>
                    <div>
                        <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                            Vente
                        </p>
                        <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>
                            {order.reference}
                        </p>
                    </div>
                    {remaining !== null && (
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                                Reste à payer
                            </p>
                            <p style={{ fontSize: 14, fontWeight: 700, color: remaining > 0 ? '#d97706' : '#059669', margin: 0 }}>
                                {formatAmount(remaining)} <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af' }}>dhs</span>
                            </p>
                        </div>
                    )}
                </div>
            )}

            <Form
                onSubmit={handleSubmit}
                defaultValues={{ quantity: 1, description: '' }}
                id="NewInvoiceForm"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
                            Mode de paiement
                        </label>
                        <Select name="paymentMethod" options={getPaymentMethodsOptions(["SPLIT_PAYMENT"])} />
                    </div>

                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
                            Montant
                        </label>
                        <InputText
                            name="invoiceAmount"
                            placeholder="0.00"
                            validationRules={{
                                required: "Le montant est requis",
                                min: { value: 1, message: "Minimum 1 dhs" },
                            }}
                        />
                    </div>
                </div>
            </Form>

            {patientId && (
                <div style={{
                    marginTop: 16,
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: 8, padding: '10px 14px',
                    fontSize: 12, color: '#1d4ed8', lineHeight: 1.5,
                }}>
                    Le paiement sera automatiquement réparti sur les ventes en attente et les dettes manuelles (solde initial), de la plus ancienne à la plus récente.
                </div>
            )}
        </Modal>
    );
};

export default NewInvoiceModal;
